import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createWorkLog,
    deleteWorkLog,
    fetchWorkLogs,
    updateWorkLog,
} from "../../api/workLogs";
import { useAuth } from "../../hooks/useAuth";
import Panel from "./Panel";
import { WORK_LOG_COPY } from "./workLogCopy";

const formatDate = (value) => {
    if (!value) return "TBD";
    // work_date is a plain YYYY-MM-DD; split it rather than going through Date,
    // which would shift the day in negative-offset timezones.
    const [y, m, d] = value.split("-");
    return `${m}/${d}/${y}`;
};

const EMPTY_DRAFT = { description: "", work_date: "", hours: "" };

const inputClass =
    "h-[40px] bg-white/60 rounded-[6px] px-3 text-[18px] text-[#1c2938] outline-none focus:ring-2 focus:ring-[#6e4e37]/40";

// Everything still awaiting review, plus the entry form. Approved logs are the
// consuming dashboard's business (its History panel), so this component
// deliberately does not render them.
export default function WorkLogPanel({ program, menuTo, className }) {
    const { isAuthenticated } = useAuth();
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [errors, setErrors] = useState(null);
    const copy = WORK_LOG_COPY[program] || WORK_LOG_COPY.internship;

    const { data: workLogs = [], isError: workLogsError } = useQuery({
        queryKey: ["workLogs", program],
        queryFn: () => fetchWorkLogs(program),
        enabled: isAuthenticated,
    });

    const inFlight = workLogs.filter((log) => log.status !== "approved");

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: ["workLogs", program] });

    // One mutation for both create and update — the draft carries an `id` only
    // when the user picked an existing row to fix.
    const saveMutation = useMutation({
        mutationFn: (payload) =>
            payload.id ? updateWorkLog(payload) : createWorkLog({ ...payload, program }),
        onSuccess: () => {
            setDraft(EMPTY_DRAFT);
            setErrors(null);
            invalidate();
        },
        onError: (error) =>
            setErrors(error.response?.data || { detail: ["Something went wrong."] }),
    });

    const deleteMutation = useMutation({
        mutationFn: deleteWorkLog,
        onSuccess: invalidate,
        onError: (error) =>
            alert("Failed to delete: " + (error.response?.data?.detail || "Unknown error")),
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        saveMutation.mutate(draft);
    };

    const startEdit = (log) => {
        setErrors(null);
        setDraft({
            id: log.id,
            description: log.description,
            work_date: log.work_date,
            hours: String(Number(log.hours)),
        });
    };

    const fieldError = (name) =>
        errors?.[name] && (
            <p className="text-[14px] text-[#c52e2e] mt-1">
                {Array.isArray(errors[name]) ? errors[name].join(" ") : String(errors[name])}
            </p>
        );

    return (
        <Panel
            title={copy.title}
            titleClass={copy.titleClass}
            menuTo={menuTo}
            className={className}
        >
            <div className="grid grid-cols-[1fr_130px_70px_190px] gap-x-4 pt-[16px] text-[16px] text-[#777f88] tracking-[0.8px]">
                <span className="pl-[28px]">{copy.column}</span>
                <span>Date</span>
                <span>Hours</span>
                <span />
            </div>
            <div className="flex-1 overflow-y-auto">
                {workLogsError ? (
                    <p className="pl-[28px] pt-[26px] text-[20px] text-[#c52e2e]">
                        Couldn&apos;t load your work log. Please try again later.
                    </p>
                ) : inFlight.length > 0 ? (
                    inFlight.map((log) => (
                        <div key={log.id} className="py-2">
                            <div className="grid grid-cols-[1fr_130px_70px_190px] gap-x-4 items-center min-h-[52px] text-[24px] text-[#5e5e5e]">
                                <span className="pl-[28px] truncate">{log.description}</span>
                                <span>{formatDate(log.work_date)}</span>
                                <span>{Number(log.hours)}</span>
                                <span className="flex items-center gap-3">
                                    <span
                                        className={`text-[16px] tracking-[0.8px] ${
                                            log.status === "rejected"
                                                ? "text-[#c52e2e]"
                                                : "text-[#777f88]"
                                        }`}
                                    >
                                        {log.status === "rejected" ? "Rejected" : "Pending"}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => startEdit(log)}
                                        className="text-[16px] text-[#1c2938] hover:underline"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => deleteMutation.mutate(log.id)}
                                        disabled={deleteMutation.isPending}
                                        className="text-[16px] text-[#c52e2e] hover:underline disabled:opacity-50"
                                    >
                                        Delete
                                    </button>
                                </span>
                            </div>
                            {log.status === "rejected" && log.rejection_reason && (
                                <p className="pl-[28px] text-[16px] text-[#c52e2e]">
                                    {log.rejection_reason}
                                </p>
                            )}
                        </div>
                    ))
                ) : (
                    <p className="pl-[28px] pt-[26px] text-[20px] text-[#777f88]">
                        Nothing awaiting review.
                    </p>
                )}
            </div>

            <form onSubmit={handleSubmit} className="pt-4 shrink-0">
                <div className="grid grid-cols-[1fr_140px_80px] gap-x-4">
                    <div>
                        <input
                            className={`${inputClass} w-full`}
                            name="description"
                            value={draft.description}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            placeholder={copy.placeholder}
                            required
                        />
                        {fieldError("description")}
                    </div>
                    <div>
                        <input
                            className={`${inputClass} w-full`}
                            type="date"
                            name="work_date"
                            value={draft.work_date}
                            onChange={(e) => setDraft({ ...draft, work_date: e.target.value })}
                            required
                        />
                        {fieldError("work_date")}
                    </div>
                    <div>
                        <input
                            className={`${inputClass} w-full`}
                            type="number"
                            // step="any" mirrors the backend's 0.01-24 range. A fixed
                            // step would anchor to min and reject ordinary values
                            // like 3.5.
                            step="any"
                            min="0.01"
                            max="24"
                            name="hours"
                            value={draft.hours}
                            onChange={(e) => setDraft({ ...draft, hours: e.target.value })}
                            placeholder="hrs"
                            required
                        />
                        {fieldError("hours")}
                    </div>
                </div>
                {errors?.detail && (
                    <p className="text-[14px] text-[#c52e2e] mt-2">
                        {Array.isArray(errors.detail)
                            ? errors.detail.join(" ")
                            : String(errors.detail)}
                    </p>
                )}
                <div className="flex justify-end items-center gap-4 mt-3">
                    {draft.id && (
                        <button
                            type="button"
                            onClick={() => {
                                setDraft(EMPTY_DRAFT);
                                setErrors(null);
                            }}
                            className="text-[16px] text-[#777f88] hover:underline"
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        type="submit"
                        disabled={saveMutation.isPending}
                        className="h-[28px] min-w-[74px] px-4 bg-[#6e4e37] text-white text-[16px] rounded-[10px] hover:bg-[#5a3f2c] transition-colors disabled:opacity-50"
                    >
                        {saveMutation.isPending ? "…" : draft.id ? "Update" : "Submit"}
                    </button>
                </div>
            </form>
        </Panel>
    );
}
